import { CompanyBrandingData } from '@customTypes/CompanyBranding';

class ColorPalletGenerator {
  static convertHexToOpacity(hex: string, opacityPercent: number) {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Convert percentage to decimal
    const alpha = opacityPercent / 100;

    // Generate different formats
    const rgba = `rgba(${r}, ${g}, ${b}, ${alpha})`;
    const hsla = ColorPalletGenerator.rgbToHsla(r, g, b, alpha);
    const hexAlpha =
      hex +
      Math.round(alpha * 255)
        .toString(16)
        .padStart(2, '0');
    const cssColor = `color: ${rgba};`;

    return {
      rgba,
      hsla,
      hexAlpha: '#' + hexAlpha,
      cssColor,
      rgb: { r, g, b },
      alpha,
    };
  }

  // Convert RGB to HSLA
  static rgbToHsla(r: number, g: number, b: number, a: number) {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number = 0,
      s: number = 0,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return `hsla(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%, ${a})`;
  }

  /**
   * Convert hex color to HSL
   * @param hexColor - Hex color (e.g., "#B000FF")
   * @returns [h, s, l] values
   */
  static hexToHsl(hexColor: string): [number, number, number] {
    // Remove # if present
    hexColor = hexColor?.replace('#', '');

    // Convert hex to RGB (0-1 range)
    const r = parseInt(hexColor.slice(0, 2), 16) / 255;
    const g = parseInt(hexColor.slice(2, 4), 16) / 255;
    const b = parseInt(hexColor.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number = 0,
      s: number = 0,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return [h, s, l];
  }

  /**
   * Convert HSL to hex color
   * @param h - Hue (0-1)
   * @param s - Saturation (0-1)
   * @param l - Lightness (0-1)
   * @returns Hex color
   */
  static hslToHex(h: number, s: number, l: number): string {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (c: number): string => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  /**
   * Generate complete color palette from base hex color
   * @param baseHex - Base hex color (e.g., "#B000FF")
   * @returns Color palette object
   */
  static generatePalette(baseHex: string): Record<string, string> {
    const [h, s, l] = this.hexToHsl(baseHex);

    // Define lightness and saturation adjustments for each shade
    // This matches your Python implementation exactly
    const shades: Record<string, [number, number]> = {
      50: [0.95, Math.max(s * 0.3, 0.1)],
      100: [0.9, Math.max(s * 0.5, 0.15)],
      200: [0.8, Math.max(s * 0.7, 0.2)],
      300: [0.7, Math.max(s * 0.85, 0.25)],
      400: [0.6, Math.max(s * 0.95, 0.3)],
      500: [l, s], // Base color
      600: [Math.max(l * 0.8, 0.3), Math.min(s * 1.1, 1.0)],
      700: [Math.max(l * 0.6, 0.25), Math.min(s * 1.15, 1.0)],
      800: [Math.max(l * 0.4, 0.2), Math.min(s * 1.2, 1.0)],
      900: [Math.max(l * 0.25, 0.15), Math.min(s * 1.25, 1.0)],
      950: [Math.max(l * 0.15, 0.1), Math.min(s * 1.3, 1.0)],
    };

    const palette: Record<string, string> = {};

    for (const [shade, [newL, newS]] of Object.entries(shades)) {
      palette[shade] = this.hslToHex(h, newS, newL);
      if (shade === '50') {
        palette['50_30'] = ColorPalletGenerator.convertHexToOpacity(
          palette[shade],
          30,
        ).hexAlpha;
      }
    }

    return palette;
  }

  /**
   * Generate CSS variables from color palettes
   * @param primaryPalette - Primary color palette
   * @param secondaryPalette - Secondary color palette
   * @returns CSS variables string
   */
  static generateCSSVariables(branding: CompanyBrandingData): string {
    const {
      primary_palette,
      secondary_palette,
      accent_palette,
      primary_color,
      secondary_color,
      accent_color,
    } = branding;

    let css = ':root {\n';

    // Primary color
    css += `  --color-primary: ${primary_color};\n`;

    // Secondary color
    css += `  --color-secondary: ${secondary_color};\n`;

    // Accent color
    css += `  --color-accent: ${accent_color};\n`;

    // Primary colors
    Object.entries(primary_palette).forEach(([shade, color]) => {
      css += `  --color-primary-${shade}: ${color};\n`;
    });

    // Secondary colors
    Object.entries(secondary_palette).forEach(([shade, color]) => {
      css += `  --color-secondary-${shade}: ${color};\n`;
    });

    // Accent colors
    Object.entries(accent_palette).forEach(([shade, color]) => {
      css += `  --color-accent-${shade}: ${color};\n`;
    });

    // Additional data
    Object.entries(branding.additional_data).forEach(([key, value]) => {
      css += `  --${key}: ${value};\n`;
    });

    css += '}\n';
    return css;
  }
}

export default ColorPalletGenerator;
